import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { isFirebaseConfigured, loadAuth, loadFirestore } from '../services/firebase';
import { getPushStatus, registerDevicePush, PushStatus } from '../utils/push';
import { useAuth } from './AuthContext';
import NewLeadToast from '../components/NewLeadToast';

export interface RealtimeEvent {
  type: 'new_lead';
  notificationId: number;
  title: string;
  body: string;
  link: string;
  won?: boolean;
  at: number;
}

export interface InboxItem {
  id: number;
  type: string;
  title: string;
  body: string | null;
  data: { spin_id?: number; wheel_id?: number; won?: boolean; link?: string };
  read_at: string | null;
  created_at: string;
}

interface RealtimeContextType {
  unviewedLeads: number;
  unreadNotifications: number;
  lastEvent: RealtimeEvent | null;
  inbox: InboxItem[];
  inboxLoading: boolean;
  hasMoreInbox: boolean;
  loadInbox: (more?: boolean) => Promise<void>;
  markNotificationsRead: (ids: number[]) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  markLeadsViewed: (spinIds: number | number[]) => Promise<void>;
  markAllLeadsViewed: () => Promise<void>;
  refreshCounts: () => Promise<void>;
  pushStatus: PushStatus;
  enablePush: () => Promise<void>;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

const INBOX_PAGE_SIZE = 20;

/**
 * Contadores e notificações em tempo real, sem polling.
 * A API publica um doc por usuário no Firestore (userSignals/{userId}) e aqui escutamos com onSnapshot.
 * Sem Firebase configurado, os contadores são buscados uma vez e ao voltar para a aba.
 */
export const RealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;

  const [unviewedLeads, setUnviewedLeads] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const [inbox, setInbox] = useState<InboxItem[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [hasMoreInbox, setHasMoreInbox] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [pushStatus, setPushStatus] = useState<PushStatus>('unsupported');

  const inboxRef = useRef<InboxItem[]>([]);
  inboxRef.current = inbox;
  const inboxLoadedRef = useRef(false);

  const refreshCounts = useCallback(async () => {
    try {
      const response = await api.get('/notifications/signals/sync');
      setUnviewedLeads(response.data.unviewedLeads || 0);
      setUnreadNotifications(response.data.unreadNotifications || 0);
    } catch (error) {
      console.error('Erro ao sincronizar notificações:', error);
    }
  }, []);

  const loadInbox = useCallback(async (more = false) => {
    try {
      setInboxLoading(true);
      const current = inboxRef.current;
      const before = more && current.length > 0 ? current[current.length - 1].id : undefined;
      const response = await api.get('/notifications/inbox', { params: { limit: INBOX_PAGE_SIZE, before } });
      const items: InboxItem[] = response.data.items || [];
      setInbox(more ? [...current, ...items] : items);
      setHasMoreInbox(items.length === INBOX_PAGE_SIZE);
      setUnreadNotifications(response.data.unread || 0);
      inboxLoadedRef.current = true;
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setInboxLoading(false);
    }
  }, []);

  // Firestore: login com custom token + escuta do doc de sinais do usuário
  useEffect(() => {
    if (!userId) return;

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    // Momento do último evento já conhecido; undefined até o primeiro snapshot.
    let lastSeenAt: number | undefined;

    const start = async () => {
      await refreshCounts();
      if (!isFirebaseConfigured) return;

      try {
        const [{ auth, signInWithCustomToken }, { db, doc, onSnapshot }] = await Promise.all([
          loadAuth(),
          loadFirestore(),
        ]);
        await auth.authStateReady();
        if (auth.currentUser?.uid !== String(userId)) {
          const response = await api.post('/auth/firebase-token');
          await signInWithCustomToken(auth, response.data.token);
        }
        if (cancelled) return;

        unsubscribe = onSnapshot(
          doc(db, 'userSignals', String(userId)),
          snapshot => {
            const data = snapshot.data();
            const event = data?.lastEvent as RealtimeEvent | undefined;

            if (data) {
              setUnviewedLeads(data.unviewedLeads || 0);
              setUnreadNotifications(data.unreadNotifications || 0);
            }

            // O primeiro snapshot traz o estado atual; só eventos mais novos que ele são novidades.
            // Atualizações só de contadores (ex.: marcar como lido) mantêm o mesmo lastEvent e não notificam.
            if (lastSeenAt === undefined) {
              lastSeenAt = event?.at ?? 0;
              return;
            }
            if (event && event.at > lastSeenAt) {
              lastSeenAt = event.at;
              setLastEvent(event);
              if (inboxLoadedRef.current) loadInbox();
              toast.custom(t => <NewLeadToast toastId={t.id} visible={t.visible} event={event} />, {
                id: `lead-${event.notificationId}`,
                duration: 6000,
                position: 'top-right',
              });
            }
          },
          error => {
            console.error('Erro na escuta em tempo real:', error);
            setIsListening(false);
          }
        );
        setIsListening(true);
      } catch (error) {
        console.error('Erro ao conectar notificações em tempo real:', error);
      }
    };

    start();

    return () => {
      cancelled = true;
      unsubscribe?.();
      setIsListening(false);
    };
  }, [userId, refreshCounts, loadInbox]);

  // Sem escuta em tempo real: atualiza ao voltar para a aba (sem intervalo)
  useEffect(() => {
    if (!userId || isListening) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshCounts();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [userId, isListening, refreshCounts]);

  // Push: status do dispositivo e renovação do token quando já autorizado
  useEffect(() => {
    if (!userId) return;
    getPushStatus().then(status => {
      setPushStatus(status);
      if (status === 'granted') {
        registerDevicePush(false).catch(error => console.error('Erro ao renovar token de push:', error));
      }
    });
  }, [userId]);

  const enablePush = useCallback(async () => {
    try {
      const status = await registerDevicePush(true);
      setPushStatus(status);
      if (status === 'granted') {
        toast.success('Notificações ativadas neste dispositivo');
      } else if (status === 'denied') {
        toast.error('Notificações bloqueadas. Libere nas configurações do navegador.');
      }
    } catch (error) {
      console.error('Erro ao ativar notificações:', error);
      toast.error('Não foi possível ativar as notificações');
    }
  }, []);

  const markNotificationsRead = useCallback(async (ids: number[]) => {
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    setInbox(items => items.map(item => (ids.includes(item.id) && !item.read_at ? { ...item, read_at: now } : item)));
    try {
      const response = await api.post('/notifications/inbox/read', { ids });
      setUnreadNotifications(response.data.unread || 0);
    } catch (error) {
      console.error('Erro ao marcar notificações como lidas:', error);
    }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    const now = new Date().toISOString();
    setInbox(items => items.map(item => (item.read_at ? item : { ...item, read_at: now })));
    setUnreadNotifications(0);
    try {
      await api.post('/notifications/inbox/read-all');
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  }, []);

  const markLeadsViewed = useCallback(async (spinIds: number | number[]) => {
    try {
      const body = Array.isArray(spinIds) ? { spin_ids: spinIds } : { spin_id: spinIds };
      await api.post('/notifications/mark-viewed', body);
      // Com Firestore o contador chega pela escuta; sem ele, busca uma vez
      if (!isListening) await refreshCounts();
    } catch (error) {
      console.error('Erro ao marcar leads como visualizados:', error);
    }
  }, [isListening, refreshCounts]);

  const markAllLeadsViewed = useCallback(async () => {
    try {
      await api.post('/notifications/mark-all-viewed');
      if (!isListening) await refreshCounts();
    } catch (error) {
      console.error('Erro ao marcar todos os leads como visualizados:', error);
    }
  }, [isListening, refreshCounts]);

  return (
    <RealtimeContext.Provider
      value={{
        unviewedLeads,
        unreadNotifications,
        lastEvent,
        inbox,
        inboxLoading,
        hasMoreInbox,
        loadInbox,
        markNotificationsRead,
        markAllNotificationsRead,
        markLeadsViewed,
        markAllLeadsViewed,
        refreshCounts,
        pushStatus,
        enablePush,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime deve ser usado dentro de RealtimeProvider');
  }
  return context;
};
